import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.pause();
    } else if (!this.paused) {
      this.play();
    }

    this.#updateButton();
  }

  disconnectedCallback() {
    this.suspend();

    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    super.disconnectedCallback?.();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    this.suspend();

    if (!this.autoplay || (this.refs.slides ?? []).length < 2) return;

    this.paused = false;
    this.#updateButton();

    this.#interval = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden || this.paused) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
    this.#updateButton();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    this.toggleAttribute('paused', value);
  }

  suspend() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused || document.hidden) return;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const value = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    if (!Number.isFinite(value) || value <= 0) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(value) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    this.#current = value;
    const index = ((value % slides.length) + slides.length) % slides.length;

    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', String(slideIndex !== index));
    });
  }

  #updateButton() {
    const button = this.querySelector('[data-announcement-toggle]');

    if (button) {
      button.textContent = this.paused ? '▶' : 'Ⅱ';
      button.setAttribute(
        'aria-label',
        this.paused
          ? 'Start announcement rotation'
          : 'Pause announcement rotation'
      );
    }

    this.setAttribute('aria-live', this.paused ? 'polite' : 'off');
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-announcement-toggle]');
    if (!button || !this.contains(button)) return;

    if (this.paused) {
      this.play();
    } else {
      this.pause();
    }
  };

  #handleMouseEnter = () => this.suspend();
  #handleMouseLeave = () => this.resume();

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}